/**
 * What the landing actions do on demo data (?mock=1): the same rules as the hub, applied to
 * the demo state, plus a ticker that moves the active landing through its stages so the board
 * is live. Pure functions over LandingState; api.ts applies them to the store.
 */
import { activeFreeze, environmentView, landingQueueId, newDeploymentId, newFreezeId, newLandingId, versionOf } from '@orgus/protocol';
import type { Deployment, DeploymentKind, DeployRequest, DeployFreezeWindow, Id, Landing, LandingProjectConfig, LandingQueue, LandingState, LandingSubmitRequest, UiErrorShape } from '@orgus/protocol';
import { ApiError } from '@orgus/ui';

const refuse = (status: number, e: UiErrorShape): never => {
  throw new ApiError({ ...e, status }, status);
};

const clone = (s: LandingState): LandingState => structuredClone(s);

function fakeSha(seed: string): string {
  let h = 2166136261;
  let out = '';
  for (let k = 0; out.length < 40; k++) {
    for (let i = 0; i < seed.length; i++) h = Math.imul(h ^ seed.charCodeAt(i) ^ k, 16777619);
    out += (h >>> 0).toString(16).padStart(8, '0');
  }
  return out.slice(0, 40);
}

export function mockSubmit(s0: LandingState, req: LandingSubmitRequest, defaultBranch: string, by: string, now: number): { state: LandingState; landing: Landing } {
  const s = clone(s0);
  const target = req.target || defaultBranch;
  if (!req.branch) refuse(400, { what: 'Pick a branch.', why: 'A landing needs the branch with the change.', next: 'Pick the branch, then submit.' });
  if (req.branch === target) refuse(400, { what: `${req.branch} is the target itself.`, why: 'A landing moves a change from its own branch onto the target.', next: 'Submit the branch the change was made on.' });
  if (s.landings.some((l) => l.projectId === req.projectId && l.source.branch === req.branch && l.target === target && ['queued', 'active', 'parked'].includes(l.status))) {
    refuse(409, { what: `${req.branch} is already in the ${target} queue.`, why: 'It is waiting or in progress.', next: 'Wait for it, or cancel it first.' });
  }
  const qid = landingQueueId(req.projectId, target);
  let q = s.queues.find((x) => x.id === qid);
  if (!q) {
    q = { id: qid, projectId: req.projectId, target, paused: false, order: [], activeId: null, waiting: null, avgMs: null, landedCount: 0, createdAt: now };
    s.queues.push(q);
  }
  const l: Landing = {
    id: newLandingId(now),
    queueId: qid,
    projectId: req.projectId,
    target,
    title: req.title?.trim() || req.branch,
    source: { branch: req.branch, ...(req.checkoutId ? { checkoutId: req.checkoutId } : {}) },
    ...(req.missionId ? { missionId: req.missionId } : {}),
    ...(req.sessionId ? { sessionId: req.sessionId } : {}),
    submittedBy: by,
    submittedAt: now,
    status: 'queued',
    stage: 'change',
    step: 'Waiting for its turn',
    machineId: null,
    jobId: null,
    attempts: 0,
    conflicts: 0,
    baseSha: null,
    headSha: null,
    landedSha: null,
    version: null,
    startedAt: null,
    endedAt: null,
    updatedAt: now,
    events: [{ at: now, stage: 'change', text: `Submitted by ${by}`, tone: 'info' }],
  };
  s.landings.push(l);
  q.order.push(l.id);
  return { state: s, landing: l };
}

function need(s: LandingState, id: Id): Landing {
  const l = s.landings.find((x) => x.id === id);
  if (!l) refuse(404, { what: 'That landing is not known.', why: 'It may have been cleared.', next: 'Reload the landing queue.' });
  return l!;
}

export function mockCancel(s0: LandingState, id: Id, by: string, now: number): LandingState {
  const s = clone(s0);
  const l = need(s, id);
  if (l.status === 'active' && (l.stage === 'push' || l.stage === 'deploy')) {
    refuse(409, { what: 'It is past the point of cancelling.', why: 'It is being pushed or deployed.', next: 'Let it finish; roll back from Environments if needed.' });
  }
  const q = s.queues.find((x) => x.id === l.queueId);
  if (q) {
    q.order = q.order.filter((x) => x !== id);
    if (q.activeId === id) q.activeId = null;
  }
  Object.assign(l, { status: 'cancelled', endedAt: now, step: `Cancelled by ${by}`, updatedAt: now });
  l.events.push({ at: now, stage: l.stage, text: `Cancelled by ${by}`, tone: 'warn' });
  return s;
}

export function mockRetry(s0: LandingState, id: Id, by: string, now: number, front = false): LandingState {
  const s = clone(s0);
  const l = need(s, id);
  if (l.landedSha) refuse(409, { what: `It is on ${l.target} already, as ${l.version}.`, why: 'Only the deploy failed.', next: 'Deploy that version from Environments.' });
  const q = s.queues.find((x) => x.id === l.queueId)!;
  Object.assign(l, { status: 'queued', stage: 'change', step: 'Waiting for its turn', conflicts: 0, endedAt: null, updatedAt: now });
  delete l.problem;
  delete l.conflictFiles;
  if (front) q.order.unshift(id);
  else q.order.push(id);
  l.events.push({ at: now, stage: 'change', text: `Retried by ${by}${front ? ', at the front of the queue' : ''}`, tone: 'info' });
  return s;
}

export function mockReorder(s0: LandingState, queueId: string, order: Id[]): LandingState {
  const s = clone(s0);
  const q = s.queues.find((x) => x.id === queueId);
  if (!q) return refuse(404, { what: 'That queue is not known.', why: 'It may have been removed.', next: 'Reload.' });
  if (order.length !== q.order.length || !order.every((id) => q.order.includes(id))) {
    refuse(409, { what: 'The queue changed while you moved things.', why: 'A landing started, finished or joined in the meantime.', next: 'Look at the new order and drag again.' });
  }
  q.order = [...order];
  return s;
}

export function mockPause(s0: LandingState, queueId: string, paused: boolean, by: string, now: number): LandingState {
  const s = clone(s0);
  const q = s.queues.find((x) => x.id === queueId)!;
  q.paused = paused;
  if (paused) Object.assign(q, { pausedBy: by, pausedAt: now });
  return s;
}

export function mockSaveConfig(s0: LandingState, config: LandingProjectConfig): LandingState {
  const s = clone(s0);
  const i = s.configs.findIndex((c) => c.projectId === config.projectId);
  if (i >= 0) s.configs[i] = config;
  else s.configs.push(config);
  return s;
}

export function mockDeploy(s0: LandingState, kind: DeploymentKind, projectId: Id, envName: string, body: DeployRequest, config: LandingProjectConfig, by: string, now: number): { state: LandingState; deployment: Deployment } {
  const s = clone(s0);
  const env = config.environments.find((e) => e.name === envName);
  if (!env) return refuse(404, { what: `There is no ${envName} environment.`, why: 'It is not in the settings.', next: 'Add it in Settings, Landing queue.' });
  const view = environmentView(projectId, env, s.deployments, now);
  let sha = '';
  let landingId: Id | null = null;
  if (kind === 'promote') {
    const from = config.environments.find((e) => e.name === body.from);
    const live = from ? environmentView(projectId, from, s.deployments, now).current : null;
    if (!live) return refuse(409, { what: `Nothing is live in ${body.from} to promote.`, why: 'It has no successful deployment.', next: 'Deploy there first.' });
    sha = live.sha;
    landingId = live.landingId;
  } else if (kind === 'rollback') {
    const to = body.to ? s.deployments.find((d) => d.projectId === projectId && d.version === body.to) : view.previous;
    if (!to) return refuse(409, { what: `There is no earlier version of ${env.name} to go back to.`, why: 'Only one version has been live here.', next: 'Deploy the version you want instead.' });
    sha = to.sha;
    landingId = to.landingId;
  } else {
    const l = s.landings.find((x) => x.projectId === projectId && x.version === body.version);
    const d = s.deployments.find((x) => x.projectId === projectId && x.version === body.version);
    if (!l?.landedSha && !d) return refuse(404, { what: 'That version is not known.', why: 'Only versions that landed through the queue can be deployed.', next: 'Pick a landed version.' });
    sha = l?.landedSha ?? d!.sha;
    landingId = l?.id ?? d!.landingId;
  }
  const version = versionOf(sha);
  if (env.protected && (body.confirm ?? '').trim().toLowerCase() !== version) {
    refuse(400, { what: 'The confirmation did not match.', why: `${env.name} is protected: type the exact version, ${version}, to confirm.`, next: `Type ${version} and confirm again.` });
  }
  const frozen = activeFreeze(env, now);
  if (frozen && kind !== 'rollback') refuse(409, { what: `${env.name} is frozen.`, why: `Freeze window: ${frozen.reason}.`, next: 'Wait for the window to end, or remove it.' });
  if (view.deploying) refuse(409, { what: `${env.name} is already deploying.`, why: 'One deployment runs at a time per environment.', next: 'Wait for it to finish.' });
  const deployment: Deployment = {
    id: newDeploymentId(now),
    projectId,
    env: env.name,
    version,
    sha,
    landingId,
    kind,
    ...(kind === 'promote' && body.from ? { fromEnv: body.from } : {}),
    ...(kind === 'rollback' && view.current ? { rollbackOf: view.current.id } : {}),
    status: 'deploying',
    by,
    startedAt: now,
    endedAt: null,
    machineId: null,
    jobId: null,
  };
  s.deployments.unshift(deployment);
  return { state: s, deployment };
}

/** Finish demo deployments that have run long enough. */
export function mockFinishDeploys(s0: LandingState, now: number, afterMs = 2500): LandingState | null {
  if (!s0.deployments.some((d) => d.status === 'deploying' && now - d.startedAt >= afterMs)) return null;
  const s = clone(s0);
  for (const d of s.deployments) {
    if (d.status !== 'deploying' || now - d.startedAt < afterMs) continue;
    for (const o of s.deployments) if (o !== d && o.projectId === d.projectId && o.env === d.env && o.status === 'live') o.status = d.kind === 'rollback' ? 'rolled-back' : 'superseded';
    d.status = 'live';
    d.endedAt = now;
  }
  return s;
}

export function mockFreeze(s0: LandingState, config: LandingProjectConfig, env: string, input: Omit<DeployFreezeWindow, 'id' | 'createdAt' | 'createdBy'>, by: string, now: number): LandingState {
  if (!input.reason.trim()) refuse(400, { what: 'The freeze window needs a reason.', why: 'People who try to deploy see it.', next: 'Say why deploys stop.' });
  const w: DeployFreezeWindow = { ...input, id: newFreezeId(now), createdBy: by, createdAt: now };
  return mockSaveConfig(s0, { ...config, environments: config.environments.map((e) => (e.name === env ? { ...e, freezes: [...e.freezes, w] } : e)) });
}

export function mockUnfreeze(s0: LandingState, config: LandingProjectConfig, env: string, id: Id): LandingState {
  return mockSaveConfig(s0, { ...config, environments: config.environments.map((e) => (e.name === env ? { ...e, freezes: e.freezes.filter((w) => w.id !== id) } : e)) });
}

const STEP_MS = 5_000;

/** Move each queue's active landing one stage on (verify, push, deploy, landed) and start the next. */
export function mockTick(s0: LandingState, now: number): LandingState | null {
  let changed = false;
  const s = clone(s0);
  for (const q of s.queues as LandingQueue[]) {
    if (q.paused) continue;
    const active = q.activeId ? s.landings.find((l) => l.id === q.activeId) : undefined;
    if (active && active.status === 'active') {
      if (now - active.updatedAt < STEP_MS) continue;
      changed = true;
      const ev = (text: string, tone: 'info' | 'ok' = 'info') => active.events.push({ at: now, stage: active.stage, text, tone });
      if (active.stage === 'verify' || active.stage === 'change') {
        active.headSha ??= fakeSha(active.id);
        Object.assign(active, { stage: 'push', step: `Pushing ${active.headSha.slice(0, 7)} to ${q.target}`, updatedAt: now });
        ev('Verify passed in 3 min 4 s', 'ok');
      } else if (active.stage === 'push') {
        const sha = active.headSha ?? fakeSha(active.id);
        Object.assign(active, { stage: 'deploy', landedSha: sha, version: versionOf(sha), step: `Deploying ${versionOf(sha)} to staging`, updatedAt: now });
        ev(`Pushed to ${q.target} as ${versionOf(sha)}`, 'ok');
      } else {
        Object.assign(active, { stage: 'landed', status: 'landed', endedAt: now, step: `Landed on ${q.target}`, updatedAt: now });
        ev(`Landed on ${q.target} as ${active.version}`, 'ok');
        q.landedCount++;
        q.activeId = null;
        if (active.landedSha) {
          for (const o of s.deployments) if (o.projectId === q.projectId && o.env === 'staging' && o.status === 'live') o.status = 'superseded';
          s.deployments.unshift({ id: newDeploymentId(now), projectId: q.projectId, env: 'staging', version: active.version!, sha: active.landedSha, landingId: active.id, kind: 'deploy', status: 'live', by: 'landing-queue', startedAt: now - 60_000, endedAt: now, machineId: active.machineId, jobId: null });
        }
      }
    } else if (q.order.length) {
      const next = s.landings.find((l) => l.id === q.order[0]);
      q.order.shift();
      if (!next) continue;
      changed = true;
      q.activeId = next.id;
      Object.assign(next, { status: 'active', stage: 'verify', step: `Running the verify command`, startedAt: now, updatedAt: now, attempts: next.attempts + 1 });
      next.events.push({ at: now, stage: 'verify', text: `Rebasing ${next.source.branch} onto ${q.target} in a fresh checkout`, tone: 'info' });
    }
  }
  return changed ? s : null;
}
